import { Box, BoxProps, Link, LinkProps, Skeleton } from "@chakra-ui/react";
import CopyableValue from "components/Shared/CopyableValue";
import { useEmailDomain } from "utils/emailDomains";

type EmailParts = {
  domain: string;
  plusSuffix?: string;
  slug: string | null | undefined;
};

function getEmailAddress({ domain, plusSuffix, slug }: EmailParts) {
  if (!domain || !slug) return;
  return `${slug}${plusSuffix ? `+${plusSuffix}` : ""}@${domain}`;
}

type BrandEmailDisplayProps = BoxProps &
  Omit<LinkProps, "href"> &
  EmailParts & {
    isLinked?: boolean;
    isLoadingDomain?: boolean;
    placeholder?: string;
  };

function BrandEmailDisplay({
  domain: emailDomain,
  isLinked,
  isLoadingDomain: isLoadingEmailDomain,
  placeholder = "[brand]",
  plusSuffix,
  slug,
  ...rest
}: BrandEmailDisplayProps) {
  const content = (
    <>
      {slug || (
        <Box as="span" opacity={0.25}>
          {placeholder}
        </Box>
      )}
      {plusSuffix ? `+${plusSuffix}` : ""}
      <Box as="span" fontWeight={300}>
        @
        <Skeleton isLoaded={!isLoadingEmailDomain} display="inline">
          {emailDomain}
        </Skeleton>
      </Box>
    </>
  );

  const emailAddress = getEmailAddress({
    domain: emailDomain,
    plusSuffix,
    slug,
  });
  if (isLinked && emailAddress) {
    return (
      <Link href={`mailto:${emailAddress}`} {...rest}>
        {content}
      </Link>
    );
  }

  return <Box {...rest}>{content}</Box>;
}

type Props = Omit<BrandEmailDisplayProps, "domain" | "isLoadingDomain">;

export default function BrandEmail({ ...rest }: Props) {
  const [emailDomain, isLoadingEmailDomain] = useEmailDomain();
  return (
    <BrandEmailDisplay
      domain={emailDomain}
      isLoadingDomain={isLoadingEmailDomain}
      {...rest}
    />
  );
}

export function CopyableBrandEmail({
  isLinked,
  isTruncated,
  placeholder,
  plusSuffix,
  slug,
  ...rest
}: Props) {
  const [emailDomain, isLoadingEmailDomain] = useEmailDomain();

  const emailAddress = getEmailAddress({
    domain: emailDomain,
    plusSuffix,
    slug,
  });

  const content = (
    <BrandEmailDisplay
      domain={emailDomain}
      isLoadingDomain={isLoadingEmailDomain}
      {...{ isLinked, isTruncated, placeholder, plusSuffix, slug }}
    />
  );

  if (!emailAddress) {
    return (
      <Box as="span" display="inline-flex" maxW="100%" {...rest}>
        {content}
      </Box>
    );
  }

  return (
    <CopyableValue
      maxW="100%"
      renderValue={() => content}
      tooltipLabel="Copy email address"
      value={emailAddress}
      {...rest}
    />
  );
}
